// Kanban-logikken uten DOM: gruppering, rekkefølge og hva som er lov å gjøre.
//
// Dra-og-slipp OG tastatur-flytting går gjennom de SAMME funksjonene her. Det er
// ikke bare pent — det er grunnen til at tastaturvarianten faktisk oppfører seg
// likt som musa. Bygger man dem hver for seg, driver de fra hverandre, og
// tastaturbrukeren får en tavle som nesten virker.
import {
  KANBAN_KOLONNER, kanbanPerKolonne,
  type Kort, type KanbanKolonne,
} from '@skade/dugnad/prosjekt';

export type PerKolonne = Record<KanbanKolonne, Kort[]>;

/** Kort gruppert og sortert per kolonne — domenepakkens sortering, ikke vår egen. */
export function grupper(kort: readonly Kort[]): PerKolonne {
  return kanbanPerKolonne(kort);
}

/**
 * Kan kortet slettes fra denne modulen?
 *
 * Nei for VALE-kort: `DELETE /kanban/kort/:id` svarer 403 på dem. UI-et skal
 * derfor ikke TILBY knappen — å vise en knapp som alltid feiler er å la
 * brukeren oppdage en regel ved å bryte den.
 */
export function kanSlettes(kort: Kort): boolean {
  return kort.kilde === 'modul';
}

/** Kolonnen til venstre/høyre, eller null i endene. */
export function nabokolonne(fra: KanbanKolonne, retning: 'venstre' | 'høyre'): KanbanKolonne | null {
  const i = KANBAN_KOLONNER.indexOf(fra);
  const j = i + (retning === 'høyre' ? 1 : -1);
  return j >= 0 && j < KANBAN_KOLONNER.length ? KANBAN_KOLONNER[j] : null;
}

/**
 * Effektive rekkefølgetall for en sortert kolonne.
 *
 * Kort uten `rekkefolge` finnes: VALE-kortene har den ikke før noen har flyttet
 * dem. De får posisjonen sin som tall. Løpende maksimum gjør rekka
 * ikke-avtakende, slik at midtpunktregningen under ikke kan bomme på et kort som
 * ligger med et lavere tall enn kortet over seg.
 */
function effektive(liste: readonly Kort[]): number[] {
  const ut: number[] = [];
  let forrige = Number.NEGATIVE_INFINITY;
  for (const [i, k] of liste.entries()) {
    const v = Math.max(k.rekkefolge ?? i + 1, forrige);
    ut.push(v);
    forrige = v;
  }
  return ut;
}

/**
 * Nytt `rekkefolge`-tall for et kort som skal inn på plass `indeks` i `liste`
 * (der `liste` er kolonnen UTEN kortet som flyttes).
 *
 * Brøktall er med vilje: da trenger vi bare å skrive ÉN PATCH, for det ene kortet
 * som ble flyttet. Alternativet — å renummerere hele kolonnen — er n kall som
 * kan feile halvveis og etterlate tavla i en tilstand ingen har bestemt.
 */
export function nyRekkefolge(liste: readonly Kort[], indeks: number): number {
  const v = effektive(liste);
  if (!v.length) return 1;
  const i = Math.max(0, Math.min(indeks, v.length));
  if (i === 0) return v[0] - 1;
  if (i >= v.length) return v[v.length - 1] + 1;
  const gap = v[i] - v[i - 1];
  // Uavgjort (to kort med samme tall) gir gap 0 — da holder vi oss like over det
  // øverste framfor å returnere et tall som er likt begge naboene.
  return v[i - 1] + (gap > 0 ? gap : 1) / 2;
}

/** Kroppen `PATCH /kanban/kort/:id` skal ha for en flytting. */
export interface Flytting {
  kortId: string;
  kolonne: KanbanKolonne;
  rekkefolge: number;
}

/**
 * Flytt et kort til `tilKolonne` på plass `tilIndeks`.
 *
 * Returnerer null når flyttingen ikke endrer noe (samme kolonne, samme plass) —
 * da skal det heller ikke sendes et kall som ville stått i revisjonssporet som
 * en endring som aldri skjedde.
 */
export function flyttKort(
  perKolonne: PerKolonne,
  kortId: string,
  tilKolonne: KanbanKolonne,
  tilIndeks: number,
): Flytting | null {
  const fraKolonne = KANBAN_KOLONNER.find((k) => perKolonne[k].some((x) => x.id === kortId));
  if (!fraKolonne) return null;
  const fraIndeks = perKolonne[fraKolonne].findIndex((x) => x.id === kortId);

  const mål = perKolonne[tilKolonne].filter((x) => x.id !== kortId);
  const grense = Math.max(0, Math.min(tilIndeks, mål.length));
  if (fraKolonne === tilKolonne && grense === fraIndeks) return null;

  return { kortId, kolonne: tilKolonne, rekkefolge: nyRekkefolge(mål, grense) };
}

/**
 * Tastaturflytting. Venstre/høyre bytter kolonne (kortet legges på samme høyde
 * det hadde), opp/ned flytter innen kolonnen.
 */
export function flyttMedTastatur(
  perKolonne: PerKolonne,
  kortId: string,
  retning: 'venstre' | 'høyre' | 'opp' | 'ned',
): Flytting | null {
  const fra = KANBAN_KOLONNER.find((k) => perKolonne[k].some((x) => x.id === kortId));
  if (!fra) return null;
  const i = perKolonne[fra].findIndex((x) => x.id === kortId);

  if (retning === 'venstre' || retning === 'høyre') {
    const til = nabokolonne(fra, retning);
    if (!til) return null;
    return flyttKort(perKolonne, kortId, til, Math.min(i, perKolonne[til].length));
  }
  const ny = retning === 'opp' ? i - 1 : i + 1;
  if (ny < 0 || ny >= perKolonne[fra].length) return null;
  return flyttKort(perKolonne, kortId, fra, ny);
}

// ── Filtrering ──────────────────────────────────────────────────────────────

export interface KortFilter {
  /** Fritekst mot tittel, notat, prosjekt og kategori. */
  søk?: string;
  sprintId?: string;
  tilordnet?: string;
  prosjekt?: string;
  kilde?: 'vale' | 'modul';
}

/** Prosjektene som finnes i kortsettet, sortert norsk. */
export function prosjekterI(kort: readonly Kort[]): string[] {
  return [...new Set(kort.map((k) => k.project))].sort((a, b) => a.localeCompare(b, 'nb'));
}

export function filtrerKort(kort: readonly Kort[], f: KortFilter): Kort[] {
  const q = (f.søk ?? '').trim().toLowerCase();
  return kort.filter((k) => {
    if (f.prosjekt && k.project !== f.prosjekt) return false;
    if (f.kilde && k.kilde !== f.kilde) return false;
    if (f.sprintId && k.sprintId !== f.sprintId) return false;
    if (f.tilordnet && k.tilordnet !== f.tilordnet) return false;
    if (!q) return true;
    return [k.title, k.note, k.project, k.category]
      .some((t) => t.toLowerCase().includes(q));
  });
}

/** Antall kort per kolonne — til kolonnehodene. */
export function antallPerKolonne(perKolonne: PerKolonne): Record<KanbanKolonne, number> {
  return Object.fromEntries(
    KANBAN_KOLONNER.map((k) => [k, perKolonne[k].length]),
  ) as Record<KanbanKolonne, number>;
}

/** Kort som er flyttet bort fra kolonnen VALE genererte dem i. */
export function overstyrteKort(kort: readonly Kort[]): Kort[] {
  return kort.filter((k) => k.overstyrt);
}
